import React, { useState } from 'react';
import { Link } from 'react-router-dom';
import ListingCard from './ListingCard';
import GradeFilter, { filterByGrade } from './GradeFilter';
import './CardShelf.css';

const MAX_VISIBLE = 24;

// Titled horizontal shelf of listing cards: heading left, PSA grade filter
// right, scroll-snap track below. Each shelf filters its own listings by grade.
// With no listings (still loading, or none yet) it holds blank space at card
// height rather than saying it's empty, so a slow load never reads as "no cards".
// `meta` is muted text after the title (e.g. a year range). `limit` caps the
// cards shown (homepage previews); listings pages pass Infinity to show all.
const CardShelf = ({ title, meta, seeAllTo, listings = [], limit = MAX_VISIBLE }) => {
    const [grades, setGrades] = useState([]);
    const [titleHover, setTitleHover] = useState(false);

    const toggleGrade = (g) =>
        setGrades((prev) => (prev.includes(g) ? prev.filter((x) => x !== g) : [...prev, g]));

    const filtered = filterByGrade(listings, grades).slice(0, limit);
    const hasItems = filtered.length > 0;

    // Inline-styled so there's no dependency on a stylesheet rule: no underline
    // by default, underline + pink on hover, always clickable to the category.
    const titleLinkStyle = {
        color: titleHover ? 'var(--gh-pink-dark)' : 'inherit',
        textDecoration: titleHover ? 'underline' : 'none',
        textUnderlineOffset: '4px',
    };

    return (
        <section className="gh-shelf">
            <div className="gh-shelf-head">
                <h2 className="gh-shelf-title">
                    {seeAllTo ? (
                        <Link
                            to={seeAllTo}
                            style={titleLinkStyle}
                            onMouseEnter={() => setTitleHover(true)}
                            onMouseLeave={() => setTitleHover(false)}
                        >
                            {title}
                        </Link>
                    ) : (
                        title
                    )}
                    {meta && <span className="gh-shelf-meta">{meta}</span>}
                    {seeAllTo && <Link to={seeAllTo} className="gh-shelf-viewall">View All</Link>}
                </h2>
                <div className="gh-shelf-controls">
                    {listings.length > 0 && (
                        <GradeFilter selected={grades} onToggle={toggleGrade} />
                    )}
                </div>
            </div>

            {hasItems ? (
                <div className="gh-shelf-track">
                    {filtered.map((l) => (
                        <ListingCard key={l.id || l.cert?.cert_number} listing={l} />
                    ))}
                </div>
            ) : listings.length === 0 ? (
                <div className="gh-shelf-placeholder" aria-hidden="true" />
            ) : (
                <p className="gh-shelf-empty">No cards match this grade.</p>
            )}
        </section>
    );
};

export default CardShelf;
